import { z } from 'zod';
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, PERMISSION_ACTIONS, PERMISSION_RESOURCES, SECURITY_EVENTS, SECURITY_LOG_PAGE_SIZE, TWO_FACTOR_BACKUP_CODE_LENGTH, TWO_FACTOR_CODE_LENGTH, USER_SORT_FIELDS, failedPasswordRules, isSupportedLocale, } from '../constants/index.js';
import { idSchema, isoDateTimeSchema } from './common.js';
import { roleKeySchema } from './enums.js';
import { paginationMetaSchema, paginationQuerySchema, sortOrderSchema } from './pagination.js';
export const permissionResourceSchema = z.enum(PERMISSION_RESOURCES);
export const permissionActionSchema = z.enum(PERMISSION_ACTIONS);
export const securityEventSchema = z.enum(SECURITY_EVENTS);
export const permissionMatrixEntrySchema = z.object({
    resource: permissionResourceSchema,
    action: permissionActionSchema,
});
/** Passwortrichtlinie: mindestens 12 Zeichen aus vier Zeichenklassen. */
export const strongPasswordSchema = z
    .string()
    .min(PASSWORD_MIN_LENGTH)
    .max(PASSWORD_MAX_LENGTH)
    .superRefine((value, ctx) => {
    for (const rule of failedPasswordRules(value)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `password.rule.${rule}`,
        });
    }
});
export const totpCodeSchema = z
    .string()
    .trim()
    .regex(new RegExp(`^\\d{${String(TWO_FACTOR_CODE_LENGTH)}}$`, 'u'), 'Erwartet werden sechs Ziffern.');
export const backupCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(new RegExp(`^[A-Z0-9]{${String(TWO_FACTOR_BACKUP_CODE_LENGTH)}}$`, 'u'), 'Erwartet wird ein Backup-Code.');
export const twoFactorMethodSchema = z.enum(['TOTP', 'EMAIL']);
// ---------------------------------------------------------------------------
// Anmeldung mit zweitem Faktor
// ---------------------------------------------------------------------------
export const twoFactorChallengeSchema = z.object({
    twoFactorRequired: z.literal(true),
    /** Kurzlebiges Token, das die bestandene Passwortprüfung ausweist. */
    challengeToken: z.string().min(20),
    expiresInSeconds: z.number().int().positive(),
    methods: z.array(twoFactorMethodSchema).min(1),
    primaryMethod: twoFactorMethodSchema,
    maskedEmail: z.string().optional(),
    resendAvailableInSeconds: z.number().int().min(0).optional(),
});
export const twoFactorVerifySchema = z
    .object({
    challengeToken: z.string().min(20),
    code: totpCodeSchema.optional(),
    backupCode: backupCodeSchema.optional(),
    method: twoFactorMethodSchema.optional(),
})
    .refine((value) => (value.code === undefined) !== (value.backupCode === undefined), 'Bitte entweder einen TOTP-Code oder einen Backup-Code angeben.');
export const twoFactorSetupResponseSchema = z.object({
    secret: z.string(),
    otpauthUri: z.string(),
    /** QR-Code als data:image/png;base64-URL. */
    qrCodeDataUrl: z.string(),
});
export const twoFactorEnableSchema = z.object({
    code: totpCodeSchema,
});
export const twoFactorBackupCodesSchema = z.object({
    backupCodes: z.array(z.string()),
});
export const twoFactorDisableSchema = z.object({
    password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
    code: totpCodeSchema,
});
export const emailTwoFactorStartSchema = z.object({
    password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
});
export const emailTwoFactorConfirmSchema = z.object({
    challengeId: idSchema,
    code: totpCodeSchema,
});
export const emailTwoFactorDisableSchema = z.object({
    password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
    secondFactor: z.union([totpCodeSchema, backupCodeSchema]),
});
export const emailTwoFactorStartResponseSchema = z.object({
    challengeId: idSchema,
    maskedEmail: z.string(),
    expiresAt: isoDateTimeSchema,
    resendAvailableInSeconds: z.number().int().min(0),
});
export const twoFactorPrimaryMethodSchema = z.object({ method: twoFactorMethodSchema });
export const twoFactorChallengeMethodSchema = twoFactorPrimaryMethodSchema.extend({
    challengeToken: z.string().min(20),
});
export const tenantTwoFactorPolicySchema = z.object({
    allowedMethods: z.array(twoFactorMethodSchema).min(1),
    isRequired: z.boolean(),
    requiredRoles: z.array(roleKeySchema),
    privilegedRolesRequireTotp: z.boolean(),
    emailEnabled: z.boolean(),
    codeValidityMinutes: z.number().int().min(5).max(15),
    resendCooldownSeconds: z.number().int().min(30).max(300),
});
export const tenantTwoFactorPolicyUpdateSchema = tenantTwoFactorPolicySchema.partial();
export const passwordChangeSchema = z.object({
    currentPassword: z.string().min(1).max(PASSWORD_MAX_LENGTH),
    newPassword: strongPasswordSchema,
});
// ---------------------------------------------------------------------------
// Sitzungen
// ---------------------------------------------------------------------------
export const sessionSchema = z.object({
    id: idSchema,
    ipAddress: z.string().nullable(),
    userAgent: z.string().nullable(),
    createdAt: isoDateTimeSchema,
    expiresAt: isoDateTimeSchema,
    lastUsedAt: isoDateTimeSchema.nullable(),
    isCurrent: z.boolean(),
});
export const sessionListSchema = z.object({
    data: z.array(sessionSchema),
});
// ---------------------------------------------------------------------------
// Rollen
// ---------------------------------------------------------------------------
export const roleSchema = z.object({
    id: idSchema,
    key: roleKeySchema,
    name: z.string(),
    description: z.string().nullable(),
    isSystem: z.boolean(),
    permissions: z.array(z.string()),
    matrix: z.array(permissionMatrixEntrySchema),
    userCount: z.number().int().min(0),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const roleListSchema = z.object({
    data: z.array(roleSchema),
});
export const roleCreateSchema = z.object({
    name: z.string().trim().min(2).max(80),
    description: z.string().trim().max(300).nullish(),
    matrix: z.array(permissionMatrixEntrySchema).default([]),
});
export const roleUpdateSchema = z.object({
    name: z.string().trim().min(2).max(80).optional(),
    description: z.string().trim().max(300).nullish(),
    matrix: z.array(permissionMatrixEntrySchema).optional(),
});
// ---------------------------------------------------------------------------
// Benutzer
// ---------------------------------------------------------------------------
export const managedUserSchema = z.object({
    id: idSchema,
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    displayName: z.string(),
    phone: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    locale: z.string(),
    roleId: idSchema,
    roleKey: roleKeySchema,
    roleName: z.string(),
    isActive: z.boolean(),
    forcePasswordChange: z.boolean(),
    isTwoFactorEnabled: z.boolean(),
    twoFactorMethods: z.array(twoFactorMethodSchema),
    primaryTwoFactorMethod: twoFactorMethodSchema.nullable(),
    isLocked: z.boolean(),
    lockedUntil: isoDateTimeSchema.nullable(),
    failedLoginAttempts: z.number().int().min(0),
    lastLoginAt: isoDateTimeSchema.nullable(),
    passwordChangedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const userListQuerySchema = paginationQuerySchema
    .omit({ sort: true })
    .extend({
    sort: z
        .string()
        .regex(/^[a-zA-Z]+:(asc|desc)$/u)
        .optional(),
    roleId: idSchema.optional(),
    isActive: z.preprocess((value) => {
        if (value === 'true') {
            return true;
        }
        if (value === 'false') {
            return false;
        }
        return value;
    }, z.boolean().optional()),
})
    .transform((value) => {
    const [field, order] = (value.sort ?? 'lastName:asc').split(':');
    const parsedField = z.enum(USER_SORT_FIELDS).safeParse(field);
    const parsedOrder = sortOrderSchema.safeParse(order);
    return {
        ...value,
        sortField: parsedField.success ? parsedField.data : 'lastName',
        sortOrder: parsedOrder.success ? parsedOrder.data : 'asc',
    };
});
export const userListResponseSchema = z.object({
    data: z.array(managedUserSchema),
    meta: paginationMetaSchema,
});
export const userCreateSchema = z.object({
    email: z.string().trim().toLowerCase().email().max(200),
    firstName: z.string().trim().min(1).max(80),
    lastName: z.string().trim().min(1).max(80),
    phone: z.string().trim().max(40).nullish(),
    locale: z
        .custom((value) => typeof value === 'string' && isSupportedLocale(value), 'Nicht unterstützte Sprache.')
        .default('tr'),
    roleId: idSchema,
    /** Leer lassen, damit das System ein konformes Initialpasswort erzeugt. */
    temporaryPassword: strongPasswordSchema.optional(),
});
export const userCreateResponseSchema = z.object({
    user: managedUserSchema,
    temporaryPassword: z.string(),
});
export const userUpdateSchema = z.object({
    firstName: z.string().trim().min(1).max(80).optional(),
    lastName: z.string().trim().min(1).max(80).optional(),
    phone: z.string().trim().max(40).nullish(),
    locale: z
        .custom((value) => typeof value === 'string' && isSupportedLocale(value), 'Nicht unterstützte Sprache.')
        .optional(),
    roleId: idSchema.optional(),
    isActive: z.boolean().optional(),
});
export const passwordResetResponseSchema = z.object({
    temporaryPassword: z.string(),
});
export const profileUpdateSchema = z.object({
    email: z.string().trim().toLowerCase().email().max(200).optional(),
    currentPassword: z.string().min(1).max(PASSWORD_MAX_LENGTH).optional(),
    firstName: z.string().trim().min(1).max(80).optional(),
    lastName: z.string().trim().min(1).max(80).optional(),
    phone: z.string().trim().max(40).nullish(),
    locale: z
        .custom((value) => typeof value === 'string' && isSupportedLocale(value), 'Nicht unterstützte Sprache.')
        .optional(),
    avatarUrl: z.string().trim().url().max(500).nullish(),
});
// ---------------------------------------------------------------------------
// Sicherheitsprotokoll
// ---------------------------------------------------------------------------
export const securityLogEntrySchema = z.object({
    id: idSchema,
    event: securityEventSchema,
    userId: idSchema.nullable(),
    userName: z.string().nullable(),
    email: z.string().nullable(),
    targetId: idSchema.nullable(),
    ipAddress: z.string().nullable(),
    userAgent: z.string().nullable(),
    metadata: z.record(z.unknown()).nullable(),
    timestamp: isoDateTimeSchema,
});
export const securityLogQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(SECURITY_LOG_PAGE_SIZE),
    q: z.string().trim().min(1).max(200).optional(),
    event: securityEventSchema.optional(),
    userId: idSchema.optional(),
    dateFrom: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/u)
        .optional(),
    dateTo: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/u)
        .optional(),
});
export const securityLogResponseSchema = z.object({
    data: z.array(securityLogEntrySchema),
    meta: paginationMetaSchema,
});
//# sourceMappingURL=security.js.map